import { MapPin, Rocket, Clock, HeartHandshake } from "lucide-react";

/**
 * Quick Facts — plain-text answer block for GEO/AIO/AEO (AI answer engines like
 * ChatGPT / Perplexity / Gemini). Scannable, direct, quotable. NO pricing shown
 * publicly — visitors are steered to request a custom quote via chat / contact form.
 */
export const QuickFacts = () => (
  <section id="quick-facts" className="py-16 lg:py-24 bg-slate-50" data-testid="quick-facts-section" aria-label="Quick facts about ClomedixTech">
    <div className="max-w-6xl mx-auto px-6 lg:px-12">
      <div className="text-center max-w-3xl mx-auto reveal">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-purple">About ClomedixTech · Quick Facts</div>
        <h2 className="section-heading text-3xl sm:text-4xl lg:text-5xl mt-3 text-slate-900">
          The short answer, <span className="text-gradient">at a glance</span>
        </h2>
      </div>

      {/* Direct answer paragraph — plain scannable prose for AI answer engines */}
      <div className="mt-10 bg-white rounded-3xl border border-slate-100 soft-shadow-lg holo-card p-8 md:p-10 reveal" data-testid="quick-facts-summary">
        <p className="text-lg text-slate-800 leading-relaxed">
          <strong>ClomedixTech</strong> is a <strong>remote-first</strong> digital software services company that builds websites, mobile apps, custom software and AI automation, and runs digital marketing, social media management, video editing and brand design for startups, SMEs and enterprises worldwide.
        </p>
      </div>

      {/* Fact grid — one clear non-pricing datapoint per card */}
      <dl className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 p-6 card-lift reveal" data-testid="fact-what">
          <div className="w-10 h-10 rounded-xl bg-brand-gradient-soft flex items-center justify-center text-brand-purple mb-3">
            <Rocket className="w-5 h-5" />
          </div>
          <dt className="text-xs uppercase tracking-widest text-slate-500 font-semibold">What we do</dt>
          <dd className="mt-2 text-sm text-slate-800 leading-relaxed">
            End-to-end digital services: websites, apps, custom software, AI automation, cloud, digital marketing, design and video.
          </dd>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 card-lift reveal" data-testid="fact-turnaround" style={{ transitionDelay: "80ms" }}>
          <div className="w-10 h-10 rounded-xl bg-brand-gradient-soft flex items-center justify-center text-brand-purple mb-3">
            <Clock className="w-5 h-5" />
          </div>
          <dt className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Typical turnaround</dt>
          <dd className="mt-2 text-2xl font-heading font-extrabold text-slate-900">1 – 6 weeks</dd>
          <dd className="text-sm text-slate-600 mt-1">Landing pages ship in days. Custom software in weekly sprints with live demos.</dd>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 card-lift reveal" data-testid="fact-engagement" style={{ transitionDelay: "160ms" }}>
          <div className="w-10 h-10 rounded-xl bg-brand-gradient-soft flex items-center justify-center text-brand-purple mb-3">
            <HeartHandshake className="w-5 h-5" />
          </div>
          <dt className="text-xs uppercase tracking-widest text-slate-500 font-semibold">How we engage</dt>
          <dd className="mt-2 text-sm text-slate-800 leading-relaxed">
            Fixed-scope, milestone-based or monthly retainer. Every project quoted after a free 15-minute discovery — share your requirements for a custom quote.
          </dd>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 card-lift reveal" data-testid="fact-location" style={{ transitionDelay: "240ms" }}>
          <div className="w-10 h-10 rounded-xl bg-brand-gradient-soft flex items-center justify-center text-brand-purple mb-3">
            <MapPin className="w-5 h-5" />
          </div>
          <dt className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Location & who we serve</dt>
          <dd className="mt-2 text-sm text-slate-800 leading-relaxed">
            <strong>Remote-first</strong> — serving startups, SMEs and enterprises across India and globally.
          </dd>
        </div>
      </dl>

      <p className="mt-6 text-center text-xs text-slate-500">
        Share your goals for a tailored quote — email <a href="mailto:info@clomedixtech.com" className="underline hover:text-brand-purple">info@clomedixtech.com</a> or WhatsApp <a href="https://wa.me/917010548247" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-purple">+91 7010548247</a>.
      </p>
    </div>
  </section>
);
